import "./src/styles/global.css"
import React from "react"
import { ApolloProvider } from "@apollo/client"
import { client } from "./src/lib/client"
import { isCredentialBearingPath } from "./src/utils/credentialBearingRoutes"
import { trackMetaPageView } from "./src/utils/metaPixel"
import { initMixpanel } from "./src/utils/mixpanel"

export const wrapRootElement = ({ element }) => (
  <ApolloProvider client={client}>{element}</ApolloProvider>
)

export const onClientEntry = () => {
  initMixpanel()
  if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual"
  }
}

// Meta Pixel PageView on every eligible route, including the initial load.
// Init may come from gatsby-ssr; trackMetaPageView lazy-inits if it did not
// (develop / session started on a credential-bearing route).
export const onRouteUpdate = ({ location }) => {
  if (isCredentialBearingPath(location?.pathname)) return
  trackMetaPageView()
}

/**
 * Gatsby only uses our value if we export this API: substitute the default.
 * Return [0,0] NO forces up: scroll-handler only checks truthiness and
 * applies the session-read position. That's why we manually scroll and false.
 *
 * @see https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/#shouldUpdateScroll
 */
export const shouldUpdateScroll = ({ routerProps, prevRouterProps }) => {
  if (!prevRouterProps) {
    return true
  }

  const propsAction = routerProps.action
  const { pathname, hash, action: locationAction } = routerProps.location
  const prevPathname = prevRouterProps.location.pathname

  if (pathname === prevPathname) {
    if (hash) {
      return decodeURI(hash.slice(1))
    }
    return true
  }

  if (propsAction === "POP" || locationAction === "POP") {
    return true
  }

  if (hash) {
    return decodeURI(hash.slice(1))
  }

  const scrollTop = () => {
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }
  scrollTop()
  window.requestAnimationFrame(scrollTop)
  return false
}

// Safari bfcache: when user navigates back (e.g. from intake), force reflow so
// header/content stacking is recomputed and overlap is avoided
if (typeof window !== "undefined") {
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      requestAnimationFrame(() => {
        document.body.offsetHeight
      })
    }
  })
}
